<script setup>
import {computed, defineProps} from "vue";
import {useNewsFeedStore} from "@/store/NewsFeed/newsFeed";
import {convertTimestampToData} from "@/tools/date-time-tools";
import { useNotificationsStore } from "@/store/notifications";
import {t} from "@/controllerLK/AvalLocalization";
import CopyIcon from "@/components/Icons/CopyIcon.vue";
import TextClamp from "@/components/UI/TextClamp.vue";

const newsFeedStore = useNewsFeedStore();
const notificationsStore = useNotificationsStore();

const props = defineProps({
  isShowFullNews: {
    type: Boolean,
    default: false,
  },
});

// Сохранения ID новости в буфер при клике.
const saveIdToClipboard = async (id) => {
  try {
    await navigator.clipboard.writeText("id: " + id);
    notificationsStore.createNotify(t("map_page.toast.clipboard_success"), "success");
  } catch (e) {
    console.log(e);
    notificationsStore.createNotify(t("map_page.toast.clipboard_error"), "success");
  }
};

// Для youtube новостей извлекаем ролик.
const extractedIframe = computed(() => {
  const raw_text = newsFeedStore.currentNews.raw_text || "";
  const iframeRegex = /<iframe[^>]*>.*?<\/iframe>/gi;
  const iframeMatch = raw_text.match(iframeRegex);
  return iframeMatch ? iframeMatch[0] : "";
});
</script>

<template>
  <div class="news-post-body">
    <!-- ====== ID и дата в полноэкранной новости располагается в заголовке  -->
    <div
      v-if="!props.isShowFullNews"
      class="news-post-body__meta"
    >
      <span class="news-post-body__meta-item news-post-body__meta-item-date">
        {{ convertTimestampToData(+newsFeedStore.currentNews.date) }}
      </span>
      <span
        v-tooltip:bottom="t('news_page_body_post.label.copy_id_tooltip')"
        class="news-post-body__meta-item news-post-body__meta-item-id"
        @click="saveIdToClipboard(newsFeedStore.currentNews.id)"
      >
        <span class="news-post-body__meta-value">
          {{ t('news_page_body_post.label.copy_id') }}
          <CopyIcon />
        </span>
      </span>
    </div>
    <!--  ID и дата в полноэкранной новости располагается в заголовке =========  -->

    <!-- ========  Для новостей, которые относятся к ютубу текст может сворачиваться.  -->
    <TextClamp
      v-if="newsFeedStore.currentNews.domain
        && newsFeedStore.currentNews.domain.includes('youtube')
        && !props.isShowFullNews"
      max-height="200"
    >
      <div
        v-dompurify-html="extractedIframe"
        class="news-post-body__content"
      />
      {{ newsFeedStore.currentNews.text }}
    </TextClamp>
    <div
      v-else
      v-dompurify-html="newsFeedStore.currentNews.raw_text"
      class="news-post-body__content"
    />
    <!-- ========  Для новостей, которые относятся к ютубу текст может сворачиваться.  -->
  </div>
</template>

<style scoped lang="scss">

.news-post-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;

  &__meta {
    display: flex;
    gap: 20px;
  }

  &__meta-item {
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    color: var(--text-text-pur);

    &-id {
      color: var(--text-text-pur);
    }
  }

  &__meta-value {
    color: var(--outline-grey3);
    cursor: default;
    margin-right: 15px;
    display: inline-flex;

    & svg {
      color: #1F9AFF;
      margin-left: 6px;
    }

    &:hover {
      cursor: default;

      & svg {
        color: #1A64D4;
      }
    }
  }

  &__content {
    display: block;
    word-break: break-word;
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
  }
}
</style>


<!-- Стили для изображений, видео, iframe применяются глобально, без scoped.  -->
<style lang="scss">
.news-post-body__content {
  img {
    display: block !important;
    max-width: 100% !important;
    height: auto !important;
    border-radius: 8px;
    margin: 10px auto;
  }

  video {
    max-width: 100% !important;
    display: block !important;
    margin: 10px auto;
  }

  iframe {
    width: 100% !important;
    max-width: 100% !important;
  }
}
</style>